import { useEffect, useState, type FormEvent } from "react";
import { Api, ApiError, type AccountInfo, type Me, type SessionInfo } from "../api.js";
import { errorText } from "../auth/Screens.js";
import { useI18n, type Lang } from "../i18n.js";
import { MfaSetup } from "../auth/Mfa.js";
import { Field, TextInput } from "./ui.js";

export function AccountPage({ api, me, onChanged, onSignedOut, onDeleted }: { api: Api; me: Me; onChanged: (me: Me) => void; onSignedOut: () => void; onDeleted: () => void }) {
  const { t, lang, setLang } = useI18n();
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [name, setName] = useState(me.user.name);
  const [accountName, setAccountName] = useState("");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [settingUp, setSettingUp] = useState(() => location.hash.includes("mfa=1") && !me.mfa.enrolled);
  const [offCode, setOffCode] = useState("");
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [note, setNote] = useState<{ key: string; ok: boolean; text: string } | null>(null);
  const say = (key: string, ok: boolean, text: string) => setNote({ key, ok, text });

  useEffect(() => {
    api.account().then((a) => {
      setAccount(a);
      setAccountName(a.name);
    }, (e) => say("account", false, errorText(e, t("common.network"))));
    api.sessions().then(setSessions, () => setSessions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, me.activeAccountId]);

  const owner = account?.yourRole === "owner";
  const message = (key: string) => (note?.key === key ? <p className={note.ok ? "ok-text" : "error"} role={note.ok ? "status" : "alert"}>{note.text}</p> : null);

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    try {
      onChanged(await api.updateMe({ name: name.trim(), language: lang }));
      say("profile", true, t("common.saved"));
    } catch (err) {
      say("profile", false, errorText(err, t("common.network")));
    }
  };

  const changeLang = async (l: Lang) => {
    setLang(l);
    try {
      onChanged(await api.updateMe({ language: l }));
    } catch {
      /* the choice still applies on this device */
    }
  };

  const savePassword = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.changePassword(current, next);
      setCurrent("");
      setNext("");
      say("password", true, t("account.passwordChanged"));
      setSessions(await api.sessions());
    } catch (err) {
      say("password", false, errorText(err, t("common.network")));
    }
  };

  const saveAccount = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.updateAccount({ name: accountName.trim(), language: lang });
      say("workspace", true, t("common.saved"));
      onChanged(await api.me());
    } catch (err) {
      say("workspace", false, errorText(err, t("common.network")));
    }
  };

  const remove = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.deleteAccount(confirm);
      onDeleted();
    } catch (err) {
      say("delete", false, err instanceof ApiError && err.code === "name_mismatch" ? err.message : errorText(err, t("common.network")));
    }
  };

  return (
    <main className="page" id="main">
      <h1>{t("account.title")}</h1>

      <section className="card" aria-labelledby="acc-profile">
        <h2 id="acc-profile">{t("account.profile")}</h2>
        <form onSubmit={saveProfile} noValidate>
          <TextInput label={t("common.name")} value={name} onChange={setName} autoComplete="name" />
          <p className="help">{me.user.email}</p>
          <Field label={t("account.language")} htmlFor="acc-lang" help={t("account.languageHelp")}>
            <select id="acc-lang" value={lang} aria-describedby="acc-lang-help" onChange={(e) => void changeLang(e.target.value as Lang)}>
              <option value="en">{t("lang.en")}</option>
              <option value="es">{t("lang.es")}</option>
            </select>
          </Field>
          <button className="btn primary" type="submit" disabled={!name.trim()}>
            {t("common.save")}
          </button>
          {message("profile")}
        </form>
      </section>

      <section className="card" aria-labelledby="acc-pass">
        <h2 id="acc-pass">{t("account.password")}</h2>
        <form onSubmit={savePassword} noValidate>
          <TextInput label={t("account.currentPassword")} type="password" autoComplete="current-password" value={current} onChange={setCurrent} />
          <TextInput label={t("account.newPassword")} type="password" autoComplete="new-password" value={next} onChange={setNext} help={t("signup.passwordHelp")} />
          <button className="btn primary" type="submit" disabled={!current || !next}>
            {t("common.save")}
          </button>
          {message("password")}
        </form>
      </section>

      <section className="card" aria-labelledby="acc-mfa">
        <h2 id="acc-mfa">{t("mfa.security")}</h2>
        {settingUp ? (
          <MfaSetup api={api} required={false} onCancel={() => setSettingUp(false)} onDone={() => void api.me().then((m) => { onChanged(m); setSettingUp(false); })} />
        ) : me.mfa.enrolled ? (
          <>
            <p>{t("mfa.on")}</p>
            <p className="hint">{t("mfa.recoveryLeft", { n: me.mfa.recoveryCodesLeft })}</p>
            <button className="btn secondary" onClick={() => void api.mfaNewRecoveryCodes().then((r) => { setNewCodes(r.recoveryCodes); void api.me().then(onChanged); }, (e) => say("mfa", false, errorText(e, t("common.network"))))}>
              {t("mfa.newCodes")}
            </button>
            <p className="hint">{t("mfa.newCodesNote")}</p>
            {newCodes && <pre className="code" tabIndex={0} aria-label={t("mfa.savedTitle")}>{newCodes.join("\n")}</pre>}
            {me.mfa.required ? (
              <p className="hint">{t("mfa.requiredNote")}</p>
            ) : (
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  void api.mfaDisable(offCode.trim()).then(
                    async () => { setOffCode(""); setNewCodes(null); onChanged(await api.me()); },
                    (err) => say("mfa", false, errorText(err, t("common.network"))),
                  );
                }}
              >
                <TextInput label={t("mfa.turnOffLead")} value={offCode} onChange={setOffCode} autoComplete="one-time-code" />
                <button className="btn secondary" type="submit" disabled={!offCode.trim()}>
                  {t("mfa.turnOff")}
                </button>
              </form>
            )}
          </>
        ) : (
          <>
            <p>{t("mfa.off")}</p>
            <button className="btn primary" onClick={() => setSettingUp(true)}>
              {t("mfa.turnOn")}
            </button>
          </>
        )}
        {message("mfa")}
      </section>

      <section className="card" aria-labelledby="acc-sessions">
        <h2 id="acc-sessions">{t("account.sessions")}</h2>
        <p>{t("account.sessionsLead")}</p>
        {sessions?.length ? (
          <ul className="plain-list">
            {sessions.map((s) => (
              <li key={s.id} className="row between">
                <span>
                  {deviceName(s.device)} · {new Date(s.lastSeenAt).toLocaleString(lang)} {s.current && <strong>({t("account.thisDevice")})</strong>}
                </span>
                {!s.current && (
                  <button className="btn ghost" onClick={() => void api.endSession(s.id).then(async () => setSessions(await api.sessions()))}>
                    {t("account.endSession")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>{t("common.loading")}</p>
        )}
        <button className="btn secondary" onClick={() => void api.logoutAll().then(onSignedOut, onSignedOut)}>
          {t("account.signOutAll")}
        </button>
      </section>

      <section className="card" aria-labelledby="acc-ws">
        <h2 id="acc-ws">{t("account.workspace")}</h2>
        {account && (
          <form onSubmit={saveAccount} noValidate>
            <TextInput label={t("account.workspaceName")} value={accountName} onChange={setAccountName} max={100} />
            {!owner && <p className="hint">{t("account.onlyOwner")}</p>}
            <button className="btn primary" type="submit" disabled={!owner || !accountName.trim()}>
              {t("common.save")}
            </button>
            {message("workspace")}
          </form>
        )}
        {message("account")}
      </section>

      {owner && account && (
        <section className="card danger" aria-labelledby="acc-del">
          <h2 id="acc-del">{t("account.delete")}</h2>
          <p>{t("account.deleteWarn", { days: account.retentionDays })}</p>
          <form onSubmit={remove} noValidate>
            <TextInput label={t("account.deleteConfirm", { name: account.name })} value={confirm} onChange={setConfirm} autoComplete="off" />
            <button className="btn danger" type="submit" disabled={confirm.trim() !== account.name}>
              {t("account.deleteButton")}
            </button>
            {message("delete")}
          </form>
        </section>
      )}
    </main>
  );
}

/** "Chrome on Windows"-style label from a user agent, without being clever about it. */
function deviceName(ua: string | null): string {
  if (!ua) return "—";
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Mac OS/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} · ${os}` : browser;
}
